/**
 * Admin Standard Detail Page
 *
 * Displays the details of an official standard including extracted clauses,
 * source document download link, and contextual action buttons based on status.
 *
 * Route: /admin/standards/$standardId
 */

// TODO: Add proper admin role check before allowing mutations

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  FileText,
  Loader2,
  Send,
  Trash2,
  XCircle,
  ArrowDownCircle,
} from "lucide-react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { Button } from "@/components/ui/primitives";
import { StateCard } from "@/components/ui";
import { useApiMutation } from "@/packages/features-shared";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

type StandardDetail = NonNullable<
  FunctionReturnType<typeof api.admin_standards_queries.getById>
>;

type StandardStatus = StandardDetail["status"];

type Clause = StandardDetail["clauses"][number];

/* ------------------------------------------------------------------ */
/* Status badge configuration (reused from StandardCard)                */
/* ------------------------------------------------------------------ */

const STATUS_CONFIG: Record<
  StandardStatus,
  { label: string; bgColor: string; textColor: string; pulse?: boolean }
> = {
  uploading: {
    label: "Uploading",
    bgColor: "rgba(161,161,166,0.15)",
    textColor: "#a1a1a6",
  },
  extracting: {
    label: "Extracting",
    bgColor: "rgba(234,179,8,0.15)",
    textColor: "#ca8a04",
    pulse: true,
  },
  extracted: {
    label: "Extracted",
    bgColor: "rgba(59,130,246,0.15)",
    textColor: "#3b82f6",
  },
  extraction_failed: {
    label: "Extraction Failed",
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
  approved: {
    label: "Approved",
    bgColor: "rgba(99,102,241,0.15)",
    textColor: "#6366f1",
  },
  generating_checklist: {
    label: "Generating Checklist",
    bgColor: "rgba(234,179,8,0.15)",
    textColor: "#ca8a04",
    pulse: true,
  },
  checklist_generation_failed: {
    label: "Checklist Generation Failed",
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
  published: {
    label: "Published",
    bgColor: "rgba(34,197,94,0.15)",
    textColor: "#22c55e",
  },
  rejected: {
    label: "Rejected",
    bgColor: "rgba(239,68,68,0.15)",
    textColor: "#ef4444",
  },
};

function StatusBadge({ status }: { status: StandardStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium ${config.pulse ? "animate-pulse" : ""}`}
      style={{ backgroundColor: config.bgColor, color: config.textColor }}
    >
      {config.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Back Link                                                            */
/* ------------------------------------------------------------------ */

function BackLink() {
  const { workspaceSlug } = useParams({ strict: false });
  return (
    <Link
      to="/$workspaceSlug/admin/standards"
      params={{ workspaceSlug: workspaceSlug ?? "" }}
      className="inline-flex items-center gap-1.5 text-sm text-secondary hover:text-primary transition-colors"
    >
      <ArrowLeft size={14} />
      Back to Standards
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Standard Detail Header                                               */
/* ------------------------------------------------------------------ */

function StandardDetailHeader({ standard }: { standard: StandardDetail }) {
  return (
    <div className="space-y-3">
      {/* Title row: template code + status badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-accent/70 shrink-0" />
          <h1 className="text-[18px] font-semibold text-primary">
            {standard.templateCode}
          </h1>
          {standard.templateDisplayName && (
            <span className="text-[14px] text-muted">
              {standard.templateDisplayName}
            </span>
          )}
        </div>
        <StatusBadge status={standard.status} />
      </div>

      {/* Info row: filename, clause count, download link */}
      <div className="flex items-center gap-4 text-sm text-secondary">
        <span className="truncate">{standard.fileName}</span>
        <span className="text-muted">
          {standard.extractedClausesCount} clauses
        </span>
        {standard.checklistItemsCount > 0 && (
          <span className="text-muted">
            {standard.checklistItemsCount} checklist items
          </span>
        )}
        {standard.documentDownloadUrl ? (
          <a
            href={standard.documentDownloadUrl}
            download
            className="inline-flex items-center gap-1 text-sm text-accent underline hover:text-accent/80 transition-colors"
          >
            <Download size={12} />
            Download Source Document
          </a>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Clause List                                                          */
/* ------------------------------------------------------------------ */

function ClauseList({ clauses }: { clauses: Clause[] }) {
  if (clauses.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted">
        No clauses extracted yet.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      {clauses.map((clause) => (
        <div
          key={clause._id}
          className={`border-b border-border/60 px-4 py-3 last:border-0 ${
            clause.parentClause ? "pl-8 bg-card/30" : "bg-card"
          }`}
        >
          <div className="text-sm font-medium text-primary">
            {clause.clauseNumber} — {clause.title}
          </div>
          <div className="mt-1 text-sm text-secondary line-clamp-3">
            {clause.description}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Standard Detail Actions                                              */
/* ------------------------------------------------------------------ */

function StandardDetailActions({
  standard,
  onAction,
  isPending,
}: {
  standard: StandardDetail;
  onAction: (
    action: "approve" | "reject" | "publish" | "unpublish" | "delete",
  ) => void;
  isPending: boolean;
}) {
  const status = standard.status;

  if (status === "extracted") {
    return (
      <div className="flex justify-end gap-3">
        <Button
          variant="danger"
          icon={XCircle}
          onClick={() => onAction("reject")}
          loading={isPending}
        >
          Reject Extraction
        </Button>
        <Button
          variant="primary"
          icon={CheckCircle}
          onClick={() => onAction("approve")}
          loading={isPending}
        >
          Approve & Generate
        </Button>
      </div>
    );
  }

  // After checklist generation completes, status goes back to "approved"
  // with checklistItemsCount > 0 — this means it's ready to publish.
  if (status === "approved" && standard.checklistItemsCount > 0) {
    return (
      <div className="flex justify-end gap-3">
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          Delete
        </Button>
        <Button
          variant="primary"
          icon={Send}
          onClick={() => onAction("publish")}
          loading={isPending}
        >
          Publish Standard
        </Button>
      </div>
    );
  }

  // "approved" without checklist items means checklist generation hasn't started/completed yet
  if (status === "approved" || status === "generating_checklist") {
    return (
      <div className="flex justify-end">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 size={14} className="animate-spin" />
          Generating checklist... please wait.
        </div>
      </div>
    );
  }

  if (status === "checklist_generation_failed") {
    return (
      <div className="flex items-center justify-end gap-3">
        {standard.extractionError && (
          <span className="text-sm text-red-500">
            {standard.extractionError}
          </span>
        )}
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          Delete
        </Button>
      </div>
    );
  }

  if (status === "published") {
    return (
      <div className="flex justify-end gap-3">
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          Delete
        </Button>
        <Button
          variant="secondary"
          icon={ArrowDownCircle}
          onClick={() => onAction("unpublish")}
          loading={isPending}
        >
          Unpublish Standard
        </Button>
      </div>
    );
  }

  if (status === "rejected" || status === "extraction_failed") {
    return (
      <div className="flex justify-end">
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          Delete
        </Button>
      </div>
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Main Page Component                                                  */
/* ------------------------------------------------------------------ */

export function StandardDetailPage() {
  const { standardId, workspaceSlug } = useParams({ strict: false });
  const navigate = useNavigate();

  const { data: standard, isLoading } = useQuery(
    convexQuery(api.admin_standards_queries.getById, {
      id: standardId as Id<"officialStandards">,
    }),
  );

  // --- Mutation hooks (useApiMutation for makeHandler Result unwrapping) ---
  const approveMutation = useApiMutation(
    api.admin_standards_mutations.approveExtraction,
    {
      name: "approve-extraction",
      successMessage: "Extraction approved. Generating checklist...",
    },
  );

  const rejectMutation = useApiMutation(
    api.admin_standards_mutations.rejectExtraction,
    { name: "reject-extraction", successMessage: "Extraction rejected" },
  );

  const publishMutation = useApiMutation(
    api.admin_standards_mutations.publish,
    { name: "publish-standard", successMessage: "Standard published" },
  );

  const unpublishMutation = useApiMutation(
    api.admin_standards_mutations.unpublish,
    { name: "unpublish-standard", successMessage: "Standard unpublished" },
  );

  const deleteMutation = useApiMutation(
    api.admin_standards_mutations.deleteStandard,
    { name: "delete-standard", successMessage: "Standard deleted" },
  );

  const isAnyMutationPending =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    deleteMutation.isPending;

  const handleAction = async (
    action: "approve" | "reject" | "publish" | "unpublish" | "delete",
  ) => {
    if (!standard) return;
    const id = standard._id;

    switch (action) {
      case "approve":
        await approveMutation.mutateAsync({ standardId: id });
        break;
      case "reject":
        await rejectMutation.mutateAsync({ standardId: id });
        navigate({
          to: "/$workspaceSlug/admin/standards",
          params: { workspaceSlug: workspaceSlug ?? "" },
        });
        break;
      case "publish":
        await publishMutation.mutateAsync({ standardId: id });
        break;
      case "unpublish":
        await unpublishMutation.mutateAsync({ standardId: id });
        break;
      case "delete":
        if (!window.confirm("Are you sure you want to delete this standard?")) {
          return;
        }
        await deleteMutation.mutateAsync({ standardId: id });
        navigate({
          to: "/$workspaceSlug/admin/standards",
          params: { workspaceSlug: workspaceSlug ?? "" },
        });
        break;
    }
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="max-w-3xl space-y-6">
        <BackLink />
        <div className="flex items-center justify-center py-16">
          <StateCard variant="loading">
            <StateCard.Icon />
            <StateCard.Title>Loading standard...</StateCard.Title>
            <StateCard.Description>
              Fetching standard details from the database.
            </StateCard.Description>
          </StateCard>
        </div>
      </div>
    );
  }

  // --- Not found state ---
  if (!standard) {
    return (
      <div className="max-w-3xl space-y-6">
        <BackLink />
        <div className="flex items-center justify-center py-16">
          <StateCard variant="empty">
            <StateCard.Icon />
            <StateCard.Title>Standard not found</StateCard.Title>
            <StateCard.Description>
              The standard you are looking for does not exist or has been
              deleted.
            </StateCard.Description>
          </StateCard>
        </div>
      </div>
    );
  }

  // --- Main content ---
  return (
    <div className="max-w-3xl space-y-6">
      <BackLink />
      <StandardDetailHeader standard={standard} />
      <ClauseList clauses={standard.clauses} />
      <StandardDetailActions
        standard={standard}
        onAction={handleAction}
        isPending={isAnyMutationPending}
      />
    </div>
  );
}
